'use client'

import { useMemo, useState } from 'react'
import { useSession } from 'next-auth/react'

import { useCartStore } from 'modules/cart'
import { extractTokenFromClientSession } from 'modules/customer'
import toast from 'shared/ui/utils/toast'
import { LocalStorage } from 'shared/utils/storage'

import { SaleOrder } from '../../../types'
import { getPaymentMethods } from '../../payment/api'
import { PaymentMethodsResponse } from '../../payment/types'
import { IS_EDIT_MODE_ENABLED } from '../config'
import { useEditSaleOrderModals } from '../providers/EditSaleOrderModalsContext'
import { EditSaleOrderCartKeys } from '../types'

import { useEditSaleOrderActions } from './useEditSaleOrderActions'

export const useBeginEditSaleOrder = () => {
  const [openingIsLoading, setOpeningIsLoading] = useState(false)
  const { cart } = useCartStore()
  const { beginEditSaleOrder } = useEditSaleOrderActions()
  const { setOrderDataFromLocalStorage } = useEditSaleOrderModals()

  const session = useSession()
  const token = useMemo(() => extractTokenFromClientSession(session), [session])

  const handleEditSaleOrder = async (saleOrder: SaleOrder) => {
    if (!IS_EDIT_MODE_ENABLED) {
      toast.error('Edycja zamówienia jest niedostępna')

      return
    }

    try {
      setOpeningIsLoading(true)

      new LocalStorage(EditSaleOrderCartKeys.EDIT_MODE_OLD_CART).set(cart)

      const newCart = await beginEditSaleOrder(saleOrder.id)

      if (!newCart?.saleOrder) {
        throw new Error('Failed to start edit mode')
      }

      const paymentMethods = await getPaymentMethods({ token })
      const paymentMethodData =
        (paymentMethods as PaymentMethodsResponse).data.data.find(
          method => method.id === saleOrder.paymentMethodId
        ) ?? saleOrder.paymentMethod

      const editModeCart = { ...newCart }

      delete editModeCart.saleOrder

      const editModeSaleOrder = { ...newCart.saleOrder }

      new LocalStorage(EditSaleOrderCartKeys.EDIT_MODE_START_CART).set(editModeCart)
      new LocalStorage(EditSaleOrderCartKeys.EDIT_MODE_START_ORDER).set(
        editModeSaleOrder
      )
      new LocalStorage(EditSaleOrderCartKeys.EDIT_MODE_START_PAYMENT_METHOD).set(
        paymentMethodData
      )

      setOrderDataFromLocalStorage()

      return true
    } catch (error) {
      console.error('Error starting edit mode:', error)

      return false
    } finally {
      setOpeningIsLoading(false)
    }
  }

  return {
    openingIsLoading,
    handleEditSaleOrder
  }
}
